import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Coins, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";

interface FirstEventBonusBannerProps {
  onCreateEvent?: () => void;
}

const FirstEventBonusBanner = ({ onCreateEvent }: FirstEventBonusBannerProps) => {
  const { user } = useAuth();
  const { locale } = useLocale();
  const [isFirstEvent, setIsFirstEvent] = useState<boolean | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const checkFirstEvent = async () => {
      if (!user) {
        setIsFirstEvent(null);
        return;
      }
      
      // Check localStorage for dismissal
      const dismissed = localStorage.getItem(`first-event-banner-dismissed-${user.id}`);
      if (dismissed) {
        setIsDismissed(true);
        return;
      }

      const { count } = await supabase
        .from("events")
        .select("*", { count: "exact", head: true })
        .eq("creator_id", user.id);
      setIsFirstEvent(count === 0);
    };
    checkFirstEvent();
  }, [user]);

  const handleDismiss = () => {
    if (user) {
      localStorage.setItem(`first-event-banner-dismissed-${user.id}`, "true");
    }
    setIsDismissed(true);
  };

  // Don't show if not logged in, not first event, or dismissed
  if (!user || !isFirstEvent || isDismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-teal-500/20 border-b border-green-500/30">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-500/20 shrink-0">
                  <Sparkles className="w-5 h-5 text-green-400 animate-pulse" />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-green-400 text-sm sm:text-base">
                      {locale.language === "pt" ? "🎁 Bônus de Primeiro Evento!" : "🎁 First Event Bonus!"}
                    </span>
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500/30">
                      <Coins className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-green-400 font-bold text-sm">+100 DC</span>
                    </div>
                  </div>
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {locale.language === "pt" 
                      ? "Crie seu primeiro evento e ganhe 100 CallyCoins!" 
                      : "Create your first event and earn 100 CallyCoins!"}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={onCreateEvent}
                  className="bg-green-500 hover:bg-green-600 text-white gap-1 hidden sm:flex"
                >
                  {locale.language === "pt" ? "Criar Evento" : "Create Event"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <button
                  onClick={handleDismiss}
                  className="p-1.5 rounded-lg hover:bg-green-500/20 transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Animated gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 via-emerald-400 to-teal-500 opacity-50" />
      </motion.div>
    </AnimatePresence>
  );
};

export default FirstEventBonusBanner;
